import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useKeyPressed } from '@/hooks/useKeyPressed';

import { CHART_ZOOM, ChartZoomStateHook } from '../components/ChartZoom';

export const useYAxisDomainFromMouseWheelZoom = (
  ref: RefObject<HTMLDivElement>,
  defaultDomain: [number, number],
  setZoom?: Pick<ChartZoomStateHook, 'setZoom'>['setZoom'],
) => {
  const { yAxisMouseWheelZoom, mouseYPosition } = useYAxisMouseWheelZoom(ref, setZoom);

  return useMemo(() => {
    if (!defaultDomain || !Array.isArray(defaultDomain) || defaultDomain.length !== 2) {
      return defaultDomain;
    }

    const min = defaultDomain[0];
    const max = defaultDomain[1];

    if (typeof min !== 'number' || typeof max !== 'number') {
      return defaultDomain;
    }

    const range = max - min;
    const zoomedRange = range / yAxisMouseWheelZoom;

    let zoomYAxisPosition = min + range / 2; // Default zoom y position is chart center

    if (mouseYPosition !== null && ref.current) {
      const chartRect = ref.current.getBoundingClientRect();
      const chartHeight = chartRect.height;

      if (mouseYPosition >= 0 && mouseYPosition <= chartHeight) {
        // Convert pixel position to domain value
        const normalizedY = 1 - mouseYPosition / chartHeight;
        zoomYAxisPosition = Math.min(max, min + normalizedY * range);
      }
    }

    const halfZoomedRange = zoomedRange / 2;
    const yMinZoomPosition = Math.max(min, zoomYAxisPosition - halfZoomedRange);
    const yMaxZoomPosition = Math.min(max, zoomYAxisPosition + halfZoomedRange);

    return [yMinZoomPosition, yMaxZoomPosition];
  }, [defaultDomain, yAxisMouseWheelZoom, mouseYPosition, ref]);
};

export const useYAxisMouseWheelZoom = (
  ref: RefObject<HTMLDivElement>,
  setZoom?: Pick<ChartZoomStateHook, 'setZoom'>['setZoom'],
) => {
  const [yAxisMouseWheelZoom, setYAxisMouseWheelZoom] = useState(1);
  const [mouseYPosition, setMouseYPosition] = useState<number | null>(null);

  const { keysPressed } = useKeyPressed(ref);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const container = ref.current;
      if (!container || !event.target) {
        return;
      }

      if (!isCtrlPressed(event, keysPressed)) {
        return;
      }

      const isEventWithinChart = container.contains(event.target as Node);
      if (!isEventWithinChart) {
        setMouseYPosition(null);
        return;
      }

      const chartRect = container.getBoundingClientRect();
      let relativeY = event.clientY - chartRect.top;

      if (relativeY < 0) {
        relativeY = 0;
      } else if (relativeY > chartRect.height) {
        relativeY = chartRect.height;
      }

      setMouseYPosition(relativeY);
    },
    [ref, keysPressed],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const container = ref.current;
      if (!container || !event.target) {
        return;
      }

      const isEventWithinChart = container.contains(event.target as Node);
      if (!isEventWithinChart) {
        return;
      }

      if (!isCtrlPressed(event, keysPressed)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      setZoom?.(CHART_ZOOM.MOUSE_WHEEL);

      const zoomSensitivity = 0.1;
      const zoomDelta = -event.deltaY * zoomSensitivity * 0.01;

      setYAxisMouseWheelZoom((prevZoom) => {
        const newZoom = prevZoom * (1 + zoomDelta);
        const clampedZoom = Math.max(0.1, Math.min(10, newZoom));
        return clampedZoom < 1 ? 1 : clampedZoom;
      });
    },
    [keysPressed, ref, setZoom],
  );

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousemove', handleMouseMove);

    return (): void => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleWheel, handleMouseMove, ref]);

  return { yAxisMouseWheelZoom, mouseYPosition };
};

const isCtrlPressed = (event: MouseEvent, keysPressed: Set<string>) =>
  event?.ctrlKey || keysPressed.has('Control') || keysPressed.has('ControlLeft') || keysPressed.has('ControlRight');
